import api from '../lib/api';

// Enums matching backend
export enum PunchType {
  CLOCK_IN = 'CLOCK_IN',
  CLOCK_OUT = 'CLOCK_OUT',
  BREAK_START = 'BREAK_START',
  BREAK_END = 'BREAK_END',
  LUNCH_START = 'LUNCH_START',
  LUNCH_END = 'LUNCH_END',
}

export enum PunchStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  EXCEPTION = 'EXCEPTION',
}

export enum PunchExceptionType {
  MISSED_CLOCK_IN = 'MISSED_CLOCK_IN',
  MISSED_CLOCK_OUT = 'MISSED_CLOCK_OUT',
  LATE_CLOCK_IN = 'LATE_CLOCK_IN',
  EARLY_CLOCK_IN = 'EARLY_CLOCK_IN',
  LATE_CLOCK_OUT = 'LATE_CLOCK_OUT',
  EARLY_CLOCK_OUT = 'EARLY_CLOCK_OUT',
  MISSED_BREAK_START = 'MISSED_BREAK_START',
  MISSED_BREAK_END = 'MISSED_BREAK_END',
  LONG_BREAK = 'LONG_BREAK',
  SHORT_BREAK = 'SHORT_BREAK',
  NO_SCHEDULED_SHIFT = 'NO_SCHEDULED_SHIFT',
  OVERTIME = 'OVERTIME',
}

export enum TimesheetStatus {
  DRAFT = 'DRAFT',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export interface TimePunch {
  id: string;
  agentId: string;
  organizationId: string;
  punchType: PunchType;
  punchTime: string;
  shiftId?: string;
  expectedTime?: string;
  isManual: boolean;
  enteredBy?: string;
  status: PunchStatus;
  exceptionType?: PunchExceptionType;
  exceptionNotes?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedReason?: string;
  createdAt: string;
  updatedAt: string;
  agent?: {
    id: string;
    firstName: string;
    lastName: string;
    employeeId: string;
  };
}

export interface PayRate {
  id: string;
  agentId: string;
  organizationId: string;
  hourlyRate: number;
  effectiveDate: string;
  endDate?: string;
  notes?: string;
  createdBy: string;
  createdAt: string;
  agent?: {
    id: string;
    firstName: string;
    lastName: string;
    employeeId: string;
  };
}

export interface Timesheet {
  id: string;
  agentId: string;
  organizationId: string;
  periodStart: string;
  periodEnd: string;
  status: TimesheetStatus;
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
  totalPay: number;
  generatedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectedReason?: string;
  createdAt: string;
  updatedAt: string;
  agent?: {
    id: string;
    firstName: string;
    lastName: string;
    employeeId: string;
  };
}

export interface CurrentStatus {
  isClocked: boolean;
  currentPunch?: TimePunch;
  lastPunch?: TimePunch;
  todayHours: number;
  weekHours: number;
}

export interface ClockInDto {
  deviceInfo?: string;
  latitude?: number;
  longitude?: number;
  agentId?: string;
}

export interface CreatePunchDto {
  agentId: string;
  punchType: PunchType;
  punchTime: string;
  shiftId?: string;
  deviceInfo?: string;
  latitude?: number;
  longitude?: number;
  isManual?: boolean;
  exceptionNotes?: string;
}

export interface ApproveExceptionDto {
  notes?: string;
}

export interface RejectExceptionDto {
  reason: string;
}

export interface CreatePayRateDto {
  agentId: string;
  hourlyRate: number;
  effectiveDate: string;
  notes?: string;
}

export interface GenerateTimesheetDto {
  agentId: string;
  periodStart: string;
  periodEnd: string;
}

export interface RejectTimesheetDto {
  reason: string;
}

export const timePunchesService = {
  // Agent operations
  clockIn: async (dto: ClockInDto = {}): Promise<TimePunch> => {
    const { data } = await api.post('/time-punches/clock-in', dto);
    return data;
  },

  clockOut: async (dto: ClockInDto = {}): Promise<TimePunch> => {
    const { data } = await api.post('/time-punches/clock-out', dto);
    return data;
  },

  startBreak: async (): Promise<TimePunch> => {
    const { data } = await api.post('/time-punches/break/start');
    return data;
  },

  endBreak: async (): Promise<TimePunch> => {
    const { data } = await api.post('/time-punches/break/end');
    return data;
  },

  startLunch: async (): Promise<TimePunch> => {
    const { data } = await api.post('/time-punches/lunch/start');
    return data;
  },

  endLunch: async (): Promise<TimePunch> => {
    const { data } = await api.post('/time-punches/lunch/end');
    return data;
  },

  getCurrentStatus: async (): Promise<CurrentStatus> => {
    const { data } = await api.get('/time-punches/current-status');
    return data;
  },

  // Admin operations
  createManualPunch: async (dto: CreatePunchDto): Promise<TimePunch> => {
    const { data } = await api.post('/time-punches/manual', dto);
    return data;
  },

  updatePunch: async (id: string, updates: Partial<TimePunch>): Promise<TimePunch> => {
    const { data } = await api.patch(`/time-punches/${id}`, updates);
    return data;
  },

  deletePunch: async (id: string): Promise<void> => {
    await api.delete(`/time-punches/${id}`);
  },

  getTodaysPunches: async (date?: string): Promise<TimePunch[]> => {
    const params = date ? { date } : {};
    const { data } = await api.get('/time-punches/today', { params });
    return data;
  },

  getPunchesByOrganization: async (startDate: string, endDate: string): Promise<TimePunch[]> => {
    const { data } = await api.get('/time-punches/organization', {
      params: { startDate, endDate },
    });
    return data;
  },

  getPunchesByAgent: async (agentId: string, startDate: string, endDate: string): Promise<TimePunch[]> => {
    const { data } = await api.get(`/time-punches/agent/${agentId}`, {
      params: { startDate, endDate },
    });
    return data;
  },

  getPendingExceptions: async (): Promise<TimePunch[]> => {
    const { data } = await api.get('/time-punches/exceptions');
    return data;
  },

  approveException: async (id: string, dto: ApproveExceptionDto): Promise<TimePunch> => {
    const { data } = await api.post(`/time-punches/exceptions/${id}/approve`, dto);
    return data;
  },

  rejectException: async (id: string, dto: RejectExceptionDto): Promise<TimePunch> => {
    const { data } = await api.post(`/time-punches/exceptions/${id}/reject`, dto);
    return data;
  },
};

export interface AgentWithPayRate {
  id: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  currentPayRate: PayRate | null;
}

export interface BulkPayRateUpdate {
  agentId: string;
  hourlyRate: number;
  effectiveDate?: string;
  notes?: string;
}

export const payRatesService = {
  getAllPayRates: async (): Promise<PayRate[]> => {
    const { data } = await api.get('/pay-rates');
    return data;
  },

  getAllAgentsWithPayRates: async (): Promise<AgentWithPayRate[]> => {
    const { data } = await api.get('/pay-rates/agents/all');
    return data;
  },

  getCurrentRate: async (agentId: string): Promise<PayRate | null> => {
    const { data } = await api.get(`/pay-rates/${agentId}/current`);
    return data;
  },

  getHistory: async (agentId: string): Promise<PayRate[]> => {
    const { data } = await api.get(`/pay-rates/${agentId}/history`);
    return data;
  },

  createPayRate: async (dto: CreatePayRateDto): Promise<PayRate> => {
    const { data } = await api.post('/pay-rates', dto);
    return data;
  },

  updatePayRate: async (id: string, updates: Partial<CreatePayRateDto>): Promise<PayRate> => {
    const { data } = await api.patch(`/pay-rates/${id}`, updates);
    return data;
  },

  deletePayRate: async (id: string): Promise<void> => {
    await api.delete(`/pay-rates/${id}`);
  },

  bulkUpdatePayRates: async (updates: BulkPayRateUpdate[]): Promise<PayRate[]> => {
    const { data } = await api.post('/pay-rates/bulk-update', { updates });
    return data;
  },
};

export const timesheetsService = {
  generateTimesheet: async (dto: GenerateTimesheetDto): Promise<Timesheet> => {
    const { data } = await api.post('/timesheets/generate', dto);
    return data;
  },

  bulkGenerateTimesheets: async (periodStart: string, periodEnd: string): Promise<Timesheet[]> => {
    const { data } = await api.post('/timesheets/bulk-generate', { periodStart, periodEnd });
    return data;
  },

  getAllTimesheets: async (status?: TimesheetStatus): Promise<Timesheet[]> => {
    const params = status ? { status } : {};
    const { data } = await api.get('/timesheets', { params });
    return data;
  },

  getTimesheetsByAgent: async (agentId: string): Promise<Timesheet[]> => {
    const { data } = await api.get(`/timesheets/agent/${agentId}`);
    return data;
  },

  approveTimesheet: async (id: string): Promise<Timesheet> => {
    const { data } = await api.post(`/timesheets/${id}/approve`);
    return data;
  },

  rejectTimesheet: async (id: string, dto: RejectTimesheetDto): Promise<Timesheet> => {
    const { data } = await api.post(`/timesheets/${id}/reject`, dto);
    return data;
  },

  deleteTimesheet: async (id: string): Promise<void> => {
    await api.delete(`/timesheets/${id}`);
  },
};
